import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import cx from "classnames";
import debounce from "lodash.debounce";
import ChevronLeft from "@bloomberg/media-ui-iconography/ChevronLeft";
import ChevronRight from "@bloomberg/media-ui-iconography/ChevronRight";

import { Tracker } from "@util/tracker";

import { useControlledState } from "../../hooks";
import { useMutationObserverEffect } from "../../hooks/useMutationObserverEffect";
import { useResizeObserverEffect } from "../../hooks/useResizeObserverEffect";

import { CarouselProps, CarouselPaginationControlsProps, CarouselWithTrackingProps } from "./types";

import styles from "./styles.module.css";

const MIN_SWIPE_DISTANCE = 50;

export const useCarouselPaginationControls =
  (): CarouselPaginationControlsProps => {
    const [controlledPageIndex, setControlledPageIndex] = useState<number>(0);

    return { controlledPageIndex, setControlledPageIndex };
  };

const useRemainingX = (listContainer, translateX) => {
  const [scrollWidth, setScrollWidth] = useState(listContainer?.scrollWidth);
  const [clientWidth, setClientWidth] = useState(listContainer?.clientWidth);

  const remainingX = useMemo(
    () => scrollWidth - clientWidth - Math.abs(translateX),
    [scrollWidth, clientWidth, translateX],
  );

  const refreshRemainingX = useCallback(
    debounce(() => {
      if (!listContainer) return;

      setScrollWidth(listContainer.scrollWidth);
      setClientWidth(listContainer.clientWidth);
    }, 100),
    [listContainer, translateX],
  );

  useMutationObserverEffect(listContainer, {
    onChildAdded: refreshRemainingX,
    onChildRemoved: refreshRemainingX,
  });
  useResizeObserverEffect(listContainer, {
    onResize: refreshRemainingX,
    onObserve: refreshRemainingX,
  });

  return { remainingX };
};

function useUpdateTileDimensionsOnResizeAndMutations(
  listContainer: HTMLUListElement,
  updateTileDimensions: ({
    updatedTileWidth,
  }: {
    updatedTileWidth: number;
  }) => void,
) {
  const firstChild = listContainer?.children[0];

  const firstChildResizeObserver = useResizeObserverEffect(
    firstChild as HTMLElement,
    {
      onResize: useCallback(
        ({ entries }) => {
          updateTileDimensions({
            updatedTileWidth: entries[0].contentRect.width,
          });
        },
        [updateTileDimensions],
      ),
    },
  );

  const resetMutationObserverOnFirstChild = useCallback(
    debounce(() => {
      firstChildResizeObserver.current.unobserve(firstChild);
      const newFirstChild = listContainer?.children[0] as HTMLElement;

      firstChildResizeObserver.current.observe(newFirstChild);
    }, 100),
    [listContainer, firstChild],
  );

  useResizeObserverEffect(listContainer, {
    onResize: useCallback(
      ({ entries }) => {
        updateTileDimensions({
          updatedTileWidth:
            entries[0].target.children?.[0]?.getBoundingClientRect().width,
        });
      },
      [updateTileDimensions],
    ),

    onObserve: useCallback(() => {
      updateTileDimensions({
        updatedTileWidth:
          listContainer?.children[0].getBoundingClientRect().width,
      });
    }, [updateTileDimensions, listContainer]),
  });

  useMutationObserverEffect(listContainer, {
    // if child is added or removed we want to reset the observer on the first child
    onChildAdded: resetMutationObserverOnFirstChild,
    onChildRemoved: resetMutationObserverOnFirstChild,
  });
}

export function Component<IterableComponentProps>(
  props: CarouselProps<IterableComponentProps>,
) {
  const {
    IterableComponent,
    componentPropsList,
    disableMouseDragScroll = false,
    tileProperties,
    paginationProperties,
    title,
    adRenderers = [],
    setControlledPageIndex,
    controlledPageIndex,
    tracker,
  } = props;
  const [listContainer, setListContainer] = useState<HTMLUListElement | null>(
    null,
  );
  const previousPageIndexRef = useRef(0);
  const [pageIndex, setPageIndex] = useControlledState<number>({
    defaultValue: 0,
    externalValue: controlledPageIndex,
    setExternalValue: setControlledPageIndex,
  });

  const [tileWidth, setTileWidth] = useState(0);
  const [tileGap, setTileGap] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const [numberOfVisibleTiles, setNumberOfVisibleTiles] = useState(1);
  const startX = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const numberOfIterableComponents =
    componentPropsList.length + adRenderers.length;

  const setListContainerOnNodeChange = useCallback(
    (node: HTMLUListElement | null) => {
      if (listContainer !== node) {
        setListContainer(node);
      }
    },
    [setListContainer, listContainer],
  );

  const numberOfColumns = props.numberOfColumns || numberOfIterableComponents;

  let numberOfPages = numberOfIterableComponents - numberOfVisibleTiles + 1;

  const { remainingX } = useRemainingX(listContainer, translateX);

  // this avoids divide by zero bug
  if (numberOfVisibleTiles) {
    numberOfPages = paginationProperties?.fullWidth
      ? Math.ceil(numberOfIterableComponents / numberOfVisibleTiles)
      : numberOfIterableComponents - numberOfVisibleTiles + 1;
  }

  const iterableComponents = componentPropsList.map((componentProp, index) => (
    <IterableComponent key={index} {...componentProp} />
  ));

  adRenderers.forEach((ad) => {
    if (ad.adPosition > 0) {
      iterableComponents.splice(ad.adPosition - 1, 0, ad.adRenderer());
    } else {
      console.error(`Invalid ad rendering position. adPosition must be >= 1`);
    }
  });

  const updateTileDimensions = useCallback(
    ({ updatedTileWidth }: { updatedTileWidth: number }) => {
      if (!listContainer) return;

      setTileWidth(tileProperties?.width || updatedTileWidth);

      setTileGap(
        tileProperties?.gap || parseInt(listContainer.style.gap, 10) || 0,
      );

      // Don't divide by 0
      if (updatedTileWidth + tileGap === 0) return;

      setNumberOfVisibleTiles(
        Math.floor(listContainer.clientWidth / (updatedTileWidth + tileGap)),
      );
    },
    [listContainer, tileGap, tileProperties?.gap, tileProperties?.width],
  );

  useUpdateTileDimensionsOnResizeAndMutations(
    listContainer,
    updateTileDimensions,
  );

  const onNavigationButtonClick = useCallback(
    (index: number) => {
      setPageIndex(index);
    },
    [pageIndex],
  );

  const onNavigationButtonForward = () => {
    setPageIndex((previousPageIndex) =>
      Math.min(previousPageIndex + 1, numberOfPages - 1),
    );
  };

  const onNavigationButtonBackward = () => {
    setPageIndex((previousPageIndex) => Math.max(previousPageIndex - 1, 0));
  };

  const translate = useCallback(() => {
    if (!listContainer) return;

    const previousPageIndex = previousPageIndexRef.current;

    if (previousPageIndex === pageIndex) return;

    // Width of the container, including content not visible on the screen due to overflow
    const scrollWidth = listContainer.scrollWidth;
    // Inner width of the container; excludes content not visible on the screen
    const clientWidth = listContainer.clientWidth;

    const incrementX = paginationProperties?.fullWidth
      ? previousPageIndex < pageIndex
        ? Math.min(
            scrollWidth - clientWidth,
            numberOfVisibleTiles * (tileWidth + tileGap) * pageIndex,
          )
        : Math.max(
            0,
            scrollWidth -
              numberOfVisibleTiles *
                (tileWidth + tileGap) *
                (numberOfPages - pageIndex),
          )
      : Math.min(scrollWidth - clientWidth, (tileWidth + tileGap) * pageIndex);

    listContainer.style.transform = `translateX(${-incrementX}px)`;

    setTranslateX(-incrementX);
  }, [
    numberOfPages,
    numberOfVisibleTiles,
    pageIndex,
    paginationProperties?.fullWidth,
    tileGap,
    tileWidth,
  ]);

  useEffect(() => {
    translate();

    previousPageIndexRef.current = pageIndex;
  }, [pageIndex, translate]);

  if (!IterableComponent) {
    console.error("No IterableComponent found for Carousel");

    return null;
  }

  if (!componentPropsList?.length) {
    console.error(`No props passed in for components in Carousel`);

    return null;
  }

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    startX.current = e.clientX;
  }, [isDragging]);

  const handleMouseUp = useCallback((e: MouseEvent) => {
    if (!isDragging || startX.current === null) {
      return;
    }

    const diff = e.clientX - startX.current;
    const swipeForward = diff < -MIN_SWIPE_DISTANCE;
    const swipeBackward = diff > MIN_SWIPE_DISTANCE;

    if (swipeForward) {
      onNavigationButtonForward();
    }

    if (swipeBackward) {
      onNavigationButtonBackward()
    }

    setIsDragging(false);
    startX.current = null;
  }, [isDragging]);

  useEffect(() => {
    if (isDragging && !disableMouseDragScroll) {
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);


  return (
    <div
      className={styles.carouselContainer}
      onMouseDown={disableMouseDragScroll ? undefined : handleMouseDown}
    >
      {title && (
        <div className={styles.header}>
          <h3 className={styles.title}>{title}</h3>
        </div>
      )}
      <ul
        className={styles.iterableComponentsContainer}
        ref={setListContainerOnNodeChange}
        style={{
          gap: tileProperties?.gap ? `${tileProperties.gap}px` : 0,
        }}
      >
        {Array(numberOfColumns)
          .fill(true)
          .map((_column, columnIndex) => (
            <li
              key={columnIndex}
              className={styles.iterableComponent}
              style={{
                width: tileProperties?.width
                  ? `${tileProperties.width}px`
                  : "100%",
              }}
            >
              {iterableComponents[columnIndex]}
            </li>
          ))}
      </ul>
      <div className={styles.navigation}>
        <div className={styles.circleButtons}>
          {Array(Math.max(numberOfPages, 0))
            .fill(true)
            .map((_, i) => (
              <span
                key={`${title}-page-${i}`}
                data-testid={`nav button ${i}`}
                className={cx(styles.circleButton, {
                  [styles.current]: pageIndex === i,
                })}
                onClick={() => onNavigationButtonClick(i)}
                data-role="nav_button"
                aria-label={`button_${i}`}
                ref={tracker?.registerTracking}
              />
            ))}
        </div>
        <div className={styles.forwardBackwardButtons}>
          <button
            className={styles.navButton}
            disabled={translateX === 0}
            onClick={() => onNavigationButtonBackward()}
            data-role="arrow"
            aria-label="Previous"
            ref={tracker?.registerTracking}
          >
            <ChevronLeft className={styles.navButtonIcon} />
          </button>
          <button
            className={styles.navButton}
            disabled={remainingX === 0}
            onClick={() => onNavigationButtonForward()}
            ref={tracker?.registerTracking}
            data-role="arrow"
            aria-label="Next"
          >
            <ChevronRight className={styles.navButtonIcon} />
          </button>
        </div>
      </div>
    </div>
  );
}
Component.displayName = "Carousel.Component";

export function WithTracking <IterableComponentProps>(props: CarouselWithTrackingProps<IterableComponentProps>) {
  const uniqueId = useId();
  const tracker = props.trackingId.length ? Tracker.getInstance() : undefined;

  return (
    <div
      id={uniqueId}
      data-component="carousel"
      data-tracking-id={props.trackingId}
      ref={tracker?.registerComponent}
    >
      <Component {...props} tracker={tracker} />
    </div>
  );
};
